import { WorkingHour } from '../../types/salary-calculator.types';
import { get } from '../api.service';

export interface DeductionRecord {
    amountPerUnit: number;
    articleNumber: string;
    customerName: string;
    houseworkType: string;
    id: number;
    invoice: Invoice[];
    invoiceId: number;
    invoiceNumber: number;
    isActive: boolean;
    number: number;
    unit: string;
    userId: number;
    workingDate: string; 
    workingDescription: string; 
    workingHour: number;
}

export interface Invoice {
    administratorFee: number | null;
    amount: number;
    amountDeductingCurrencyMargin: number;
    amountInSEK: number;
    amountIncVat: number;
    createdBy: number;
    createdByEmail: string | null;
    createdDate: string;
    creditInvoiceNumber: number;
    creditOriginInvoiceNumber: number;
    currency: string;
    currencyRate: number;
    customer: any | null;
    customerId: number;
    description: string;
    dueDate: string;
    employeeSalary: any | null;
    estimatedNetSalary: number | null;
    expressPayout: boolean;
    expressPayoutAmount: number;
    fakturaNuFee: number;
    feePercentage: number;
    foreignBankFee: number;
    fortnoxInvoiceNumber: number;
    holidaySavings: boolean;
    holidaySavingsAmount: number;
    holidaySavingsFee: number;
    hourlyWagesAmountPerUnit: number | null;
    id: number;
    incomeTaxReductionInSek: number;
    invoiceFileUpload: any[];
    invoiceFreelancer: any[];
    invoiceNumber: number;
    invoicePurposeType: number;
    invoiceSalaryStatus: number;
    invoiceStageForUserView: number;
    invoiceStatus: number;
    invoiceType: number;
    isActive: boolean;
    isAuthorizedForReminder: boolean;
    isBooked: boolean;
    isCreditInvoiceRequested: boolean | null;
    isEditInvoiceRequestApproved: boolean;
    isEditInvoiceRequestRejected: boolean;
    isEditInvoiceRequested: boolean;
    isEmailSent: boolean;
    isFileuploadedOnFortnox: boolean;
    isFixed: boolean;
    isHaveCreditInvoice: boolean;
    isInvoiceAddedOnFortnox: boolean;
    isInvoiceCanceledOnFortnox: boolean;
    isInvoiceUpdatedOnFortnox: boolean;
    isPfdAttached: boolean;
    isReminderInvoice: boolean;
    isReverseVatApplied: boolean;
    isSalaryCreated: boolean;
    isSalaryPaidThroughInvoiceStatus: boolean;
    isSalaryStatusUpdatedManually: boolean;
    isTaxReductioncreatedOnFortnox: boolean;
    isUserUploadedToComplyCube: boolean | null;
    minimumFee: number;
    multipleFreelancer: boolean;
    notes: boolean;
    ocrNumber: number;
    pension: boolean;
    pensionAmount: number;
    pensionFee: number;
    pensionFeePer: number;
    registerAssignment: boolean;
    relatedInvoice: any | null;
    relatedInvoiceId: number | null;
    reminderInvoiceNumber: number | null;
    reverseVatType: any | null;
    rotAccommodationDeductionType: any | null;
    rotDeduction: boolean;
    rutDeduction: boolean;
    salaryFreelancer: any | null;
    showDates: boolean;
    showHours: boolean;
    socialFeePer: number;
    socialFeePercentage: number;
    tax: number;
    taxReductionFortnoxId: number | null;
    taxfreeArticle: any | null;
    toPayAmont: number;
    totalAllowanceAbroadTaxFree: number | null;
    totalAllowanceAbroadTaxFreeNotShown: number | null;
    totalAllowanceAbroadTaxable: number | null;
    totalAllowanceAbroadTaxableNotShown: number | null;
    totalCurrencyMargin: number | null;
    totalDomesticAllowanceFullDayTaxFree: number | null;
    totalDomesticAllowanceFullDayTaxFreeNotShown: number | null;
    totalDomesticAllowanceFullDayTaxable: number | null;
    totalDomesticAllowanceFullDayTaxableNotShown: number | null;
    totalDomesticAllowanceHalfDayTaxFree: number | null;
    totalDomesticAllowanceHalfDayTaxFreeNotShown: number | null;
    totalDomesticAllowanceHalfDayTaxable: number | null;
    totalDomesticAllowanceHalfDayTaxableNotShown: number | null;
    totalExpenditureForMaterialsAndToolsAccordingToAttachedPdfTaxFreeNotShown: number | null;
    totalExpenditureForMaterialsAndToolsAccordingToAttachedPdfTaxable: number | null;
    totalExpenditureForMaterialsAndToolsAccordingToAttachedPdfTaxableNotShown: number | null;
    totalExpenditureForMaterialsAndToolsTaxable: number | null;
    totalGrossAmount: number | null;
    totalHourlyWagesHours: number | null;
    totalMileageTaxFree: number | null;
    totalMileageTaxFreeNotShown: number | null;
    totalMileageTaxable: number | null;
    totalMileageTaxableNotShown: number | null;
    totalPensionContribution: number | null;
    totalPostFeeAmount: number;
    totalRealPensionAmount: number | null;
    totalSocialFee: number | null;
    totalTravelAllowance: number | null;
    totalTravelCostsAccordingToAttachedPdfTaxFree: number | null;
    totalTravelCostsAccordingToAttachedPdfTaxFreeNotShown: number | null;
    userName: string;
    userSSN_DOB: string | null;
    vat: number;
    vatAmount: number | null;
    workingHours: WorkingHour[];
}

export interface DeductionParams {
    PageNumber: number;
    PageSize: number;
}

export interface DeductionResponse {
    statusCode: number;
    message: string;
    response: DeductionRecord[];
    totalCount?: number;
    numberOfPages?: number;
}

/**
 * Fetches all salary deductions with pagination and filtering.
 * Corresponds to: GET /api/Salary/deduction
 * @param params - The request parameters containing PageNumber, PageSize, fromDate, toDate, and searchQuery.
 * @returns The API response with deduction data.
 */
export const fetchSalaryDeductions = async (
    params: DeductionParams
): Promise<DeductionResponse> => {
    try {
        const queryParams: any = {
            PageNumber: params.PageNumber,
            PageSize: params.PageSize,
        };

        const response = await get('/salarynoticev2/deduction', queryParams);
        return response;
    } catch (error) {
        console.error('Error fetching salary deductions:', error);
        throw error;
    }
};